<template>
    <div class="h--100 relative min-h-inherit">
        <div class="page--wrap" :class="{ 'disabled': deleteLoading }">
            <template v-if="pageLoading">
                <SkeletonPageList class="mt--40" />
            </template>

            <template v-else-if="fetchErr">
                <EmptyPage state="error" @action="pageReload = true" />
            </template>
            
            <template v-else>
                <div v-if="showActionBtns" class="flex align-items-center justify-content-between w-100" :class="{ 'disabled': checkUpgradeLoading && checkUpgradeFields.projects }">                        
                    <div>
                        <p class="table__striped__footer__results">
                            COUNT: <span>{{ projects.length }}</span>
                        </p>
                    </div>
                    <div class="flex align-items-center gap-2">
                        <BaseButton 
                            v-if="selectedProjects.length > 0" 
                            variant="warning" 
                            is-content
                            @submit="toggleDelete"
                        >
                            <template #content>
                                Delete {{ selectedProjects.length }} {{ selectedProjects.length === 1 ? 'project' : 'projects' }}
                            </template>
                        </BaseButton>

                        <DateRangeFilter page="projects" />

                        <MainFilter page-type="projects" />

                        <SortFilter
                            size="sm" :value="displayType" label-value="Title"
                            @setType="setDisplayType"
                        />

                        <BaseButton 
                            label="+ Add project"
                            :class="{ 'disabled': checkUpgradeLoading && checkUpgradeFields.projects }" 
                            @submit="initiateCreate"
                        />

                        <DropDownButton 
                            id="projectActions"
                            variant="outline"
                            prefix-icon
                            icon-name="dots-horizontal-rounded" 
                            variant-style="secondary"
                            size="sm"
                        >
                            <template #dropdown-content>
                                <ListItem>
                                    <span class="text--sm text-gray-800">Kanban view</span>
                                    <div class="flex">
                                        <span class="tag bold text--xxs tag--grey mt--5">Coming soon!</span>
                                    </div>
                                </ListItem>
                            </template>
                        </DropDownButton>
                    </div>
                </div>

                <div class="w-100" :class="{ 'min-h-inherit justify-content-center flex align-items-center': !projects.length }">
                    <template v-if="!projects.length">
                        <EmptyPage v-if="isSearched" state="no_result" />

                        <EmptyPage
                            v-else 
                            state="empty_custom" 
                            btn-state="custom" 
                            icon-name="layer"
                            type="project" 
                            position="left"
                            title="No projects created" 
                            subtitle="Start an active database of projects associated with clients. Projects can be paid or free and scheduled to start whenever you want."
                        >
                            <template #button>
                                <BaseButton 
                                    label="+ New project"
                                    :disabled="checkUpgradeLoading && checkUpgradeFields.projects"
                                    @submit="initiateCreate"
                                />
                            </template>
                        </EmptyPage>
                    </template>

                    <template v-else>
                        <div class="flex mt--40">
                            <CustomTable
                                :columns="projectHeaders"
                                :data="projects"
                                :selected-for-deletion="selectedProjects"
                                router-name="project-details-view"
                                is-action-btns 
                                table-type="striped"
                                is-row-link
                                class="mb--0"
                                @onRowClick="openDetailsPage"
                                @onRowSelected="selectProjects"
                            >
                                <template #rows="data">
                                    <td class="first">
                                        {{ data.title || '-' }}
                                    </td>
                                    <td>
                                        <Status type="project" :value="data.status" />
                                    </td>
                                    <td>{{ $format_date(data.deadline) || '-' }}</td>
                                    <td class="text--capitalize">
                                        {{ data.projectType || '-' }}
                                    </td>
                                    <td class="text--capitalize">
                                        {{ data.priority || '-' }}
                                    </td>
                                    <td>{{ $format_date(data.createdAt) || "-" }}</td>
                                </template>
                            </CustomTable>
                        </div>

                        <div class="table__striped__footer mt--5">
                            <pagination v-if="showPagination" :pagination-data="pageData" />
                        </div>
                    </template>
                </div>
            </template>
        </div>

        <CreateProjectModal
            :show-modal="!!createProjectModal" 
            :loading="createLoading"
            :current-selection="createProjectModal"
            @close="createProjectModal = ''" 
            @complete="handleCreateProject"
        />

        <ConfirmDeletion
            type="project" custom-msg
            :show-modal="showDeleteModal" :items="selectedProjects"
            :loading="deleteLoading"
            @cancel="showDeleteModal = false" @delete="deleteMultipleProjects"
        >
            <template #custom-msg>
                <p class="text--sm">
                    The selected item(s) will be removed from all related clients and invoices. <strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal || showUpgradeModalAlt" 
            type="projects" 
            @cancel="closeUpgradeModal" 
        />

        <StartProjectModal :show-modal="showStartProjectModal" :project-id="createdProjectId" @close="showStartProjectModal = false" />
    </div>
</template>

<script>
import {
    MainFilter,
    SortFilter,
    Pagination,
    EmptyPage,
    CreateProjectModal,
    ConfirmDeletion,
    BaseButton,
    CustomTable,
    UpgradeAccountModal,
    Status,
    ListItem,
    DateRangeFilter,
} from "@/components/ui/index";
import { assembleQueryList } from "@/utils/helpers";
import { projectHeaders } from "@/utils/dummy";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import DropDownButton from "@/components/ui/buttons/DropDownButton.vue";
import StartProjectModal from "@/components/ui/modals/StartProjectModal.vue";
import SkeletonPageList from "@/components/ui/loaders/SkeletonPageList.vue";

export default {
    name: "ProjectsPage",

    components: {
        CreateProjectModal,
        MainFilter,
        ConfirmDeletion,
        SortFilter,
        Pagination,
        EmptyPage,
        BaseButton,
        CustomTable,
        UpgradeAccountModal,
        Status,
        ListItem,    
        DropDownButton,
        StartProjectModal,
        SkeletonPageList,
        DateRangeFilter,
    },

    data() {
        return {
            displayType: "",
            selectedProjects: [],
            createProjectModal: "",
            selectedProject: null,
            createLoading: false,
            deleteLoading: false,
            fetchErr: false,
            fetchErrMsg: "",
            showDeleteModal: false,
            projectHeaders,
            pageLoading: false,
            downloadType: "",
            showUploadModal: false,
            uploadProgress: 0,
            uploadContent: [],
            uploadErr: false,
            downloadLoading: false,
            showUpgradeModalAlt: false,
            pageReload: false,
            showStartProjectModal: false,
            createdProjectId: "",
        };
    },

    computed: {
        pageData() {
            return this.$store.getters["project/pageData"];
        },

        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }

            if (this.isSearched) {
                return true;
            }
            
            return false;
        },
        
        projects() {
            return this.$store.getters["project/projects"];
        },

        clients() {
            return this.$store.getters["clients/clients"];
        },

        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {                
                await this.handleFetchProjects({ ...newVal }, true);
            },
            deep: true,
        },

        displayType(newType, oldType) {
            if (newType !== oldType) {
                this.sortProjects();
            }
        },

        pageReload: {
            async handler(newVal) {                
                if (newVal === true) {
                    await this.handleFetchProjects({...this.$route.query }, true);
                }
            },
        },
    },

    async mounted() {
        if (!this.projects.length) {
            await this.handleFetchProjects({ ...this.$route.query }, true);
        }
    },

    methods: {
        initiateCreate() {
            this.createProjectModal = "create_non_paid";
        },

        closeUpgradeModal() {
            if (this.isSearched) {
                this.$store.dispatch("app/closeUpgradeModal", "projects");
            } else {
                this.showUpgradeModalAlt = false;
            }
        },

        openDetailsPage(row) {
            if (!row?._id) return;

            this.$router.push(`/dashboard/projects/${row._id}`);
        },

        setDownloadType(val) {
            this.downloadClients(val);
        },

        setDisplayType(val) {
            this.displayType = val;
        },

        toggleDelete() {
            this.showDeleteModal = !this.showDeleteModal;
        },

        sortProjects() {
            this.$store.dispatch("project/sortProjects", this.displayType);
        },

        async handleCreateProject({ payload, resetFn }) {
            try {
                this.createLoading = true;

                const resp = await this.$store.dispatch("project/addProject", { payload });
                
                if (resetFn) {
                    resetFn();
                }

                if (this.projects.length === 1) {
                    this.$store.dispatch("app/getOnboardingChecks").catch(e => console.log("caught", e));
                }
                
                this.createdProjectId = resp._id;
                this.createProjectModal = "";

                if (resp?.status === "in progress") {
                    return this.$router.push({ name:"project-details-view", params: { id: resp?._id }}); 
                }

                this.showStartProjectModal = true;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                if (errMsg.includes("upgrade your account")) {
                    this.createProjectModal = "";
                    this.showUpgradeModalAlt = true;
                    return;
                }
                this.$toast.error(errMsg);
            } finally {
                this.createLoading = false;
            }
        },

        async handleFetchProjects(params, refresh) {
            try {                                
                this.pageLoading = true;
                this.fetchErr = false;
                this.fetchErrMsg = "";
                this.pageReload = false;
                
                const pageQueryObj = {
                    page: params?.page || 1,
                    type: params?.type || "active",
                    ...params,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                
                await this.$store.dispatch("project/fetchProjects", { params: buildResult, refresh });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.fetchErr = true;
                this.fetchErrMsg = errMsg;
            } finally {
                this.pageLoading = false;
            }
        }, 

        async deleteMultipleProjects() {
            try {
                this.deleteLoading = true;

                await this.$store.dispatch("project/deleteProjects", this.selectedProjects);
                this.$toast.success(`You deleted ${this.selectedProjects.length} ${this.selectedProjects.length === 1 ? "project": "projects"}`);

                this.$store.dispatch("app/fetchCompletedProjectCount", { refresh: true });
                
                this.selectProjects([]);
                this.showDeleteModal = false;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },

        selectProjects(val) {
            this.selectedProjects = val;
        },
    },
};
</script>